import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  Eye,
  Globe,
  Linkedin,
} from "lucide-react";
import { useState } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { ChannelBadge } from "./ChannelBadge";
import { fetchLeadboxerSessions } from "./leadboxer/api";
import type { EnrichedLead, LeadboxerEvent, LeadboxerInstance } from "./leadboxer/types";
import {
  formatUnixTime,
  formatUnixTimestamp,
  getLeadDisplayName,
  getLeadEvents,
  getLeadVisits,
} from "./leadboxer/utils";

function getEventTitle(event: LeadboxerEvent): string {
  if (event.page_type === "referrer") {
    if (event.session_referrer_hostname) {
      return event.session_referrer_hostname;
    }
    if (event.session_referrer) {
      try {
        return new URL(event.session_referrer).hostname || event.session_referrer;
      } catch {
        return event.session_referrer;
      }
    }
    return "Unknown referrer";
  }

  if (event.title) return event.title;
  if (event.root_url) {
    try {
      return new URL(event.root_url).pathname || event.root_url;
    } catch {
      return event.root_url;
    }
  }
  return "Unknown event";
}

function getEventUrl(event: LeadboxerEvent): string | null {
  if (event.page_type === "referrer") {
    return event.session_referrer ?? null;
  }
  return event.root_url ?? null;
}

function getEventType(event: LeadboxerEvent): "referrer" | "event" {
  return event.page_type === "referrer" ? "referrer" : "event";
}

function getEventDuration(event: LeadboxerEvent): string {
  if (event.page_type === "referrer") return "s.e.";
  return event.duration || "5s";
}

export const VisitorLeadModal = ({
  lead,
  instance,
  open,
  onOpenChange,
}: {
  lead: EnrichedLead | null;
  instance: LeadboxerInstance;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const [displayMode, setDisplayMode] = useState<"title" | "url">("title");

  const leadCardLink = lead?.["lead card link"];

  const { data: sessionsWithEvents = [], isPending, error } = useQuery({
    queryKey: ["visitor-insights", "sessions", instance, leadCardLink],
    queryFn: () => fetchLeadboxerSessions(instance, leadCardLink!),
    enabled: open && !!leadCardLink,
  });

  if (!lead) return null;

  const name = getLeadDisplayName(lead);
  const logo = lead.organizationLogoUrl || lead.leadLogoUrl;
  const industry = lead.organizationIndustryName || "-";
  const employees = lead.organizationEmployeeCountRangeName || "-";
  const city = lead.organizationCity || "-";
  const country = lead.organizationCountry || "-";
  const phone = lead.organizationPhone || "-";
  const domain = lead.organizationDomain || "-";
  const linkedin = lead.organizationLinkedinUrl || "";
  const score = lead.leadScore ?? "-";
  const tags = lead.organization_tags ?? [];
  const visits = getLeadVisits(lead);
  const events = getLeadEvents(lead);
  const lastChannel = (lead.last_channel || "-") as string;
  const lastActivity = lead.prettyLastEvent || "-";

  const getSessionDate = (session: Record<string, unknown>) => {
    const ts =
      session.session_start_unix_timestamp ||
      session.eventUnixTimestamp ||
      session.eventEsTimestamp;
    const formatted = formatUnixTimestamp(String(ts ?? ""));
    return formatted && formatted !== "Invalid Date" ? formatted : "Unknown Date";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-8">
            <div className="flex items-center gap-4 min-w-0">
              {logo ? (
                <img
                  src={String(logo)}
                  alt=""
                  className="size-12 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="size-12 rounded-full bg-muted flex items-center justify-center shrink-0 font-semibold text-muted-foreground">
                  {name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              )}
              <div className="min-w-0">
                <DialogTitle className="truncate">{name}</DialogTitle>
                <p className="text-sm text-muted-foreground">{industry}</p>
              </div>
            </div>
            {lead.crmCompany ? (
              <Button asChild size="sm" variant="outline" className="shrink-0">
                <Link to={`/companies/${lead.crmCompany.id}`}>
                  <Building2 className="size-4 mr-1" />
                  {translate("crm.visitor_insights.view_customer", {
                    _: "View customer",
                  })}
                </Link>
              </Button>
            ) : null}
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-6 pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.industry", { _: "Industry" })}
                </div>
                <div>{industry}</div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.employees", { _: "Employees" })}
                </div>
                <div>{employees}</div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.location", { _: "Location" })}
                </div>
                <div>
                  {city}, {country}
                </div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.phone", { _: "Phone" })}
                </div>
                <div>{phone}</div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.website", { _: "Website" })}
                </div>
                {domain !== "-" ? (
                  <a
                    href={`https://${domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Globe className="size-4" />
                    {domain}
                  </a>
                ) : (
                  <div>{domain}</div>
                )}
              </div>
              <div>
                <div className="font-medium text-muted-foreground">LinkedIn</div>
                {linkedin ? (
                  <a
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Linkedin className="size-4" />
                    View Profile
                  </a>
                ) : (
                  <div>-</div>
                )}
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.score", { _: "Score" })}
                </div>
                <div>{score}</div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.fields.tags", { _: "Tags" })}
                </div>
                <div>
                  {Array.isArray(tags) && tags.length > 0 ? tags.join(", ") : "-"}
                </div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.columns.visits", { _: "Visits" })}
                </div>
                <div>{visits}</div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.columns.events", { _: "Events" })}
                </div>
                <div>{events}</div>
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.columns.last_channel", {
                    _: "Last Channel",
                  })}
                </div>
                <ChannelBadge channel={lastChannel} />
              </div>
              <div>
                <div className="font-medium text-muted-foreground">
                  {translate("crm.visitor_insights.columns.last_activity", {
                    _: "Last Activity",
                  })}
                </div>
                <div>{lastActivity}</div>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">
              {translate("crm.visitor_insights.session_events", {
                _: "Session Events",
              })}
            </h3>

            {isPending ? (
              <div className="flex items-center gap-2 py-8 text-muted-foreground">
                <Spinner className="size-4" />
                <span>
                  {translate("crm.visitor_insights.loading_sessions", {
                    _: "Loading sessions...",
                  })}
                </span>
              </div>
            ) : null}

            {error ? (
              <p className="text-destructive text-sm">
                {error instanceof Error ? error.message : "Failed to load sessions"}
              </p>
            ) : null}

            {!isPending && !error && sessionsWithEvents.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {translate("crm.visitor_insights.no_sessions", {
                  _: "No sessions found for this lead.",
                })}
              </p>
            ) : null}

            {!isPending && !error && sessionsWithEvents.length > 0 ? (
              <div className="space-y-4">
                {sessionsWithEvents.map(({ session, events: sessionEvents }) => (
                  <div key={session.session_id} className="border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 px-4 py-3 border-b flex items-center justify-between gap-2">
                      <h4 className="font-medium">{getSessionDate(session)}</h4>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant={displayMode === "title" ? "default" : "outline"}
                          onClick={() => setDisplayMode("title")}
                        >
                          Title
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={displayMode === "url" ? "default" : "outline"}
                          onClick={() => setDisplayMode("url")}
                        >
                          URL
                        </Button>
                      </div>
                    </div>
                    <div>
                      {sessionEvents.map((event, eventIndex) => {
                        const eventTime = formatUnixTime(
                          event.eventUnixTimestamp ||
                            event.event_timestamp ||
                            "",
                        );
                        const eventTitle = getEventTitle(event);
                        const eventUrl = getEventUrl(event);
                        const eventType = getEventType(event);
                        const eventDuration = getEventDuration(event);
                        const displayText =
                          displayMode === "title"
                            ? eventTitle
                            : eventUrl || eventTitle;
                        const isClickable = displayMode === "title" && !!eventUrl;

                        return (
                          <div
                            key={`${session.session_id}-${eventIndex}`}
                            className="flex items-start gap-4 px-4 py-3 hover:bg-muted/30 border-b last:border-b-0"
                          >
                            <div className="shrink-0 w-24">
                              <div className="text-xs font-medium">
                                {eventType === "referrer" ? "Referrer" : "Event"}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {eventTime}
                              </div>
                            </div>
                            <div
                              className={`size-6 rounded-full flex items-center justify-center shrink-0 mt-1 ${
                                eventType === "referrer"
                                  ? "bg-green-500"
                                  : "bg-primary"
                              }`}
                            >
                              {eventType === "referrer" ? (
                                <ArrowRight className="size-3 text-white" />
                              ) : (
                                <Eye className="size-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  {isClickable ? (
                                    <a
                                      href={eventUrl!}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm font-medium text-primary hover:underline"
                                    >
                                      {displayText}
                                    </a>
                                  ) : (
                                    <span className="text-sm font-medium">
                                      {displayText}
                                    </span>
                                  )}
                                  {displayMode === "title" && eventUrl ? (
                                    <div className="text-xs text-muted-foreground truncate">
                                      {eventUrl}
                                    </div>
                                  ) : null}
                                </div>
                                <span className="text-xs text-muted-foreground shrink-0">
                                  {eventDuration}
                                </span>
                              </div>
                              {event.exit_title === "Account Tags added" &&
                              event.customEventInfo?.LB_SysEvt_Acc_Tags ? (
                                <span className="text-xs text-green-700 font-semibold">
                                  +{String(event.customEventInfo.LB_SysEvt_Acc_Tags)}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
